import React, { useState } from 'react';
import { BsChevronLeft, BsChevronRight, BsPrinter } from 'react-icons/bs';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import PDFMerger from 'pdf-merger-js/browser';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setScheduleDate, getSchedule, getTripTicket, resetGetTripTicketl } from '../driversSlice';
import { getDriverV2 } from '../../DriversV2/driversV2Slice';
import { ScheduleTable } from './ScheduleTable';
import { DriverNav } from './DriverNav';
import { DriverDetailsCard, DailyProgressTab, DocumentsTab, PunchCardsTab, SafetyTab } from '../components';
import { BackButton } from '../../../components';
import { base64toBlobPdf } from '../../../lib/base64ToBlobPdf';

import './Schedule.scss';
import { ScheduleItemType } from '../../../models';

type TabId = 'daily-progress' | 'schedules' | 'punch-cards' | 'safety' | 'documents';

export function Schedule() {
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const driverId = params.id;

  const driver = useAppSelector(state => state.driversV2.drivers.find(d => d.id === driverId));
  const selectedDriver = useAppSelector(state => state.driversV2.selectedDriver);
  const currentDriver = driver || selectedDriver;

  React.useEffect(() => {
    if (!driver && driverId) {
      dispatch(getDriverV2(driverId));
    }
  }, [driverId, driver]);
  const scheduleDate = useAppSelector(state => state.drivers.scheduleDate);
  const updateOrderAPI = useAppSelector(state => state.drivers.updateScheduleItemOrder);
  const getScheduleAPI = useAppSelector(state => state.drivers.getSchedule);
  const getTripTicketAPI = useAppSelector(state => state.drivers.getTripTicket);
  const schedule = useAppSelector(state => state.drivers.schedule);

  const [dateMoment, setDateMoment] = React.useState<moment.Moment>(moment(scheduleDate));
  const [date, setDate] = React.useState<string>(dateMoment.format('dddd, Do MMMM'));
  const [activeTab, setActiveTab] = useState<TabId>('daily-progress');

  const tabs = [
    { id: 'daily-progress' as TabId, label: 'Daily Progress' },
    { id: 'schedules' as TabId, label: 'Schedules' },
    { id: 'punch-cards' as TabId, label: 'Punch Cards' },
    { id: 'safety' as TabId, label: 'Safety' },
    { id: 'documents' as TabId, label: 'Documents' },
  ];

  React.useEffect(() => {
    return () => {
      dispatch(resetGetTripTicketl());
    };
  }, []);

  React.useEffect(() => {
    if (!!currentDriver && activeTab === 'schedules') {
      dispatch(getSchedule({
        date: scheduleDate,
        driverId: currentDriver.userId
      }));
    }
  }, [currentDriver, scheduleDate, activeTab]);

  React.useEffect(() => {
    if (!!currentDriver && !updateOrderAPI.loading && !!updateOrderAPI.value && activeTab === 'schedules') {
      dispatch(getSchedule({
        date: scheduleDate,
        driverId: currentDriver.userId
      }));
    }
  }, [updateOrderAPI.loading, updateOrderAPI.value, activeTab]);

  React.useEffect(() => {
    const createPDF = async () => {
      if (!getTripTicketAPI.value) return null;
      
      const pdfFiles = getTripTicketAPI.value.map(pdf =>base64toBlobPdf(pdf));

      const merger = new PDFMerger();
      for(const file of pdfFiles) {
        await merger.add(file);
      }

      const mergedPdf = await merger.saveAsBlob();

      return mergedPdf;
    };

    if (!getTripTicketAPI.loading && !!getTripTicketAPI.value) {
      createPDF()
        .then(pdf => {
          const fileURL = URL.createObjectURL(pdf!);
          //Open the URL on new Window
          const pdfWindow = window.open();
          pdfWindow!.location.href = fileURL;
        })
        .catch((err) => {
          throw err;
        });
    }
  }, [getTripTicketAPI.loading, !!getTripTicketAPI.value]);

  if (!currentDriver) return null;
  
  const onDayBeforeClick = () => {
    setDateMoment(dateMoment.add(-1, 'day'));
    setDate(dateMoment.format('dddd, Do MMMM'));
    dispatch(setScheduleDate(dateMoment.format('YYYY-MM-DD')));
  };
  
  const onDayAfterClick = () => {
    setDateMoment(dateMoment.add(1, 'day'));
    setDate(dateMoment.format('dddd, Do MMMM'));
    dispatch(setScheduleDate(dateMoment.format('YYYY-MM-DD')));
  };

  const onPrintClick = () => {
    if (!schedule) return;

    const orderIds = schedule.items
      .filter(i => i.type === ScheduleItemType.DELIVERY)
      .map(i => i.scheduleItemId);

    dispatch(getTripTicket({
      id: schedule.scheduleId,
      orderIds
    }));
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'daily-progress':
        return (
          <div className='tab-content'>
            <DailyProgressTab userId={currentDriver.userId} driverId={currentDriver.id} />
          </div>
        );
      case 'schedules':
        return (
          <div className='tab-content'>
            <div className='schedule-header-container'>
              <div className='date-container'>
                <div className='icon-container' onClick={onDayBeforeClick}>
                  <BsChevronLeft className='icon'/>
                </div>
                <div className='icon-container' onClick={onDayAfterClick}>
                  <BsChevronRight className='icon'/>
                </div>

                <div className='date-text'>{date}</div>
                {getScheduleAPI.loading && <div style={{ marginLeft: 8}}><Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/></div>}
              </div>

              <div className='print-button' onClick={onPrintClick}>
                <BsPrinter style={{ marginRight: 8 }}/>
                Print
                {getTripTicketAPI.loading && <div style={{ marginLeft: 8}}><Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/></div>}
              </div>
            </div>

            <ScheduleTable />
          </div>
        );
      case 'punch-cards':
        return (
          <div className='tab-content'>
            <PunchCardsTab userId={currentDriver.userId} driverName={currentDriver.name} />
          </div>
        );
      case 'safety':
        return (
          <div className='tab-content'>
            <SafetyTab userId={currentDriver.userId} />
          </div>
        );
      case 'documents':
        return (
          <div className='tab-content'>
            <DocumentsTab driverId={currentDriver.id} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className='schedule-component'>
      <div className='schedule-header'>
        <BackButton onClick={() => navigate(-1)} />
        <DriverNav driverId={currentDriver.id} />
      </div>

      <DriverDetailsCard driver={currentDriver} />

      <div className='tabs-container'>
        <div className='nav-tabs'>
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
}
